import type {
  CheckboxChangeEventType,
  DataGridRef,
  DialogRef,
  DynamicFormButtonSetting,
  DynamicFormField,
  DynamicFormRef,
  FieldChangedFunction,
  JsonData,
  JsonDataItem,
  JsonDataWrapper,
  OdinDataRetrieval,
  OdinDataRetrievalOptions,
  OdinDataUpdate,
  OdinKey,
  PostSubmitHandler,
  PreSubmitHandler,
} from '@myosh/odin-components';
import {
  DataSearchLocation,
  DynamicForm,
  DynamicFormRequiredType,
  IconButton,
  OdinDataSender,
  OdinIconSize,
  Tooltip,
  useTestTag,
} from '@myosh/odin-components';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import clsx from 'clsx';
import { cloneDeep, debounce, isArray, uniq } from 'lodash';
import type { Ref } from 'react';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { BehaviorSubject, firstValueFrom, Subject, take, takeUntil } from 'rxjs';
import { v4 } from 'uuid';
import type { AttachmentsPayloadProps, AttachmentsRemove } from '../../@types/attachments';
import type { GenericErrorResponse } from '../../@types/common';
import type {
  CombinedFieldType,
  ExtendedDynamicFormNextWorkflowStep,
  ExtendedDynamicFormSettings,
  ExtendedDynamicFormWorkflowStep,
} from '../../@types/forms';
import type { HierarchyDropdownValue } from '../../@types/hierarchy-fields';
import type { QuestionnaireFieldValue } from '../../@types/questionnaire';
import type { RecordResult, RecordStructure } from '../../@types/records';
import { isDbRecordId, isOfflineEnabled } from '../../db/odin-db-util';
import useActiveRecordRef from '../../hooks/use-active-record-ref';
import { useAnalysisBackgroundJobHandler } from '../../hooks/use-analysis-background-job-handler';
import { useApiLogger } from '../../hooks/use-api-logger';
import useDynamicFormNotifier from '../../hooks/use-dynamic-form-notifier';
import useIsMetadataKey from '../../hooks/use-is-metadata-key';
import usePerformingSaveToast from '../../hooks/use-performing-save-toast';
import useProfileData from '../../hooks/use-profile-data';
import { useRecordFieldVisibilities } from '../../hooks/use-record-field-visibilities';
import useFetchWorkflowSteps from '../../hooks/use-workflow-steps';
import { useAppDispatch } from '../../redux/hooks';
import type { SyncDetails } from '../../redux/middleware/sync-transforms';
import { useFormAccessControlQuery, useFormSettingsQuery, useGetFeatureToursQuery } from '../../redux/services/api';
import { fileApi } from '../../redux/services/file';
import {
  recordApi,
  useGetDefaultRecordValuesQuery,
  useGetRecordLinkFieldsDefaultValuesQuery,
  useLazyGetRecordByIdQuery,
} from '../../redux/services/record';
import { getDataGridReference } from '../../services/data-grid.service';
import { showError, showInfo, showSuccess, showWarning } from '../../services/notification.service';
import { getRecordReference } from '../../services/record.service';
import { extractFormGroupFieldsByFieldType } from '../../utils/dynamic-form-utils';
import {
  currentWorkflowStep,
  customPropertyOfFormGroups,
  formGroupPermissions,
  getFormGroupPersonFieldPermissions,
  workflowStepCanProgressPermission,
} from '../../utils/form-permissions-util';
import { invalidateRecordSpecificTags, invalidateSharedTags } from '../../utils/invalidate-cache-tags-utils';
import { forceAssert } from '../../utils/type-guards';
import type { ActiveRecordContextProps } from '../active-record/active-record.component';
import SingleRecordAiAnalysis from '../ai-analysis/single-record-ai-analysis.component';
import CloseActiveItemButton from '../common/close-active-item-button';
import ConfirmationDialog from '../common/confirmation-dialog.component';
import AIField from '../fields/ai-field/ai-field.component';
import { HfAttachmentsField } from '../fields/attachments-field/hf-attachments-field.component';
import HfCloneRecordButtonField from '../fields/clone-record-button-field/hf-clone-record-button-field.component';
import HfComboboxMultiselect from '../fields/combobox/hf-combobox-multiselect.component';
import HfCombobox from '../fields/combobox/hf-combobox.component';
import HfEmailThisButton from '../fields/email-this-button/hf-email-this-button.component';
import HfExportButtonField from '../fields/export-button-field/hf-export-button-field.component';
import { HfHierarchyField } from '../fields/hierarchy/hf-hierarchy-field.component';
import HfInvitationField from '../fields/invitation-field/hf-invitation-field.component';
import HfInviteButtonField from '../fields/invite-button-field/hf-invite-button-field.component';
import HfLinkField from '../fields/link/hf-link-field.component';
import { createLinkedRecordToasterId } from '../fields/linked-records/components/create-linked-record';
import HfLinkedRecords from '../fields/linked-records/hf-linked-records.component';
import HfMultiselectCheckbox from '../fields/multiselect-checkbox/hf-multiselect-checkbox.component';
import HfOptionGroup from '../fields/option-group/hf-option-group.component';
import HfMultiselectPersonField from '../fields/person/hf-multiselect-person-field.component';
import HfPersonField from '../fields/person/hf-person-field.component';
import HfReadersField from '../fields/person/hf-readers-field.component';
import HfQuestionnaire from '../fields/questionnaire/hf-questionnaire.component';
import HfReverseLinkedRecords from '../fields/reverse-linked-records/hf-reverse-linked-records.component';
import HfRiskRating from '../fields/risk-rating/hf-risk-rating.component';
import HfSummaryRecordLink from '../fields/summary-record-link/hf-summary-record-link.component';
import { summaryRecordLinkFieldTypes } from '../fields/summary-record-link/summary-record-link.functions';
import HfTrainingField from '../fields/training/hf-training-field.component';
import HfTwinColumnSelect from '../fields/twin-column-select/hf-twin-column-select-field.component';
import HfUserRecordLink from '../fields/user-record-link/hf-user-record-link.component';
import HfVersionField from '../fields/version/hf-version-field.component';
import { FormGroupNavigation } from '../form/form-group-navigation.component';
import { FormLoading } from '../form/form-loading.component';
import { FormTitle } from '../form/form-title.component';
import RecordSideBarFeatureTour from '../layout/components/feature-tour-guides/record-sidebar-feature-tour';
import type { ConflictResolution, ConflictsModalRef } from './conflicts-modal/conflicts-modal.component';
import ConflictsModal from './conflicts-modal/conflicts-modal.component';
import useGetRecord from './hooks/use-get-record';
import useRecordPermissionsAndMode from './hooks/use-record-permissions-and-mode';
import RecordAccountable from './record-accountable.component';
import { RecordMenu } from './record-menu.component';
import RecordOffline from './record-offline.component';
import { RecordTitle } from './record-title.component';
import {
  addAccessControlConditionsAsVisibilityConditions,
  buildWorkflowStepVisibilityConditions,
  cleanupRecordData,
  constructRecordTitle,
  constructRecordTitleElement,
  createPatchData,
  createSaveData,
  errorMessage,
  findFieldRecursiveSearch,
  findMergeConflicts,
  resolveRecordStatus,
  updateOriginalData,
} from './util/record-component-util';
import { getRecordFieldOptions } from './util/record-field-options';
import { validateByFieldType } from './util/record-fields-validation';
import { BACKGROUND_SAVE_KEY, mutateRecord } from './util/record-mutate';
import { applySubmitTransforms } from './util/record-submit-transforms';
import type { WorkflowStepModalRef } from './workflowstep-modal/workflowstep-modal.component';
import WorkflowStepModal from './workflowstep-modal/workflowstep-modal.component';

export const RecordSaveState = {
  Update: 0,
  New: 1,
} as const;
export type RecordSaveState = (typeof RecordSaveState)[keyof typeof RecordSaveState];

export interface RecordRef {
  recordId?: string;
  openerRecordId?: string;
  scrollFieldIntoView: (scrollId: string) => void;
  refreshRecord: () => void;
}

export interface RecordProps {
  recordId?: string;
  formId: number;
  title?: string;
  vikingLinkUrl?: string;
  recordState?: RecordSaveState;
  viewId?: string;
  moduleIcon?: string;
  openInEditMode?: boolean;
  openerRecordId?: string;

  onClose: (id: string) => void;
  panelSettings?: BehaviorSubject<ActiveRecordContextProps>;
  onCloseActiveRecordConfirmationModalRef?: DialogRef | null;

  isLinkedRecord?: boolean;
  isTextualQuestionnaireLinkedRecords?: boolean;
  reverseLinkedRecords?: Array<RecordResult>;
  reverseRecordLinkFormId?: number;
  isPreview?: boolean;
  isReadOnly?: boolean;
  isSimpleView?: boolean;
  showFormGroupsNavigation?: boolean;
  onLinkedRecordCreated?: (recordId: number) => void;
  onSimpleViewRecordCreated?: (recordId: string) => void;
  parentLinkedRecordId?: number;
  parentLinkedFieldId?: number;
  hideFormGroupsNavigation?: boolean;
  showCloseButton?: boolean;
  showEditButton?: boolean;
}

function Record(
  {
    recordId,
    formId,
    title,
    vikingLinkUrl,
    recordState = RecordSaveState.Update,
    viewId,
    moduleIcon,
    openInEditMode = false,
    openerRecordId,
    onClose,
    panelSettings,
    onCloseActiveRecordConfirmationModalRef,
    isLinkedRecord,
    reverseLinkedRecords,
    reverseRecordLinkFormId,
    isPreview,
    isReadOnly = false,
    isSimpleView = false,
    showFormGroupsNavigation = false,
    onLinkedRecordCreated,
    onSimpleViewRecordCreated,
    parentLinkedRecordId,
    parentLinkedFieldId,
    isTextualQuestionnaireLinkedRecords,
    showCloseButton,
    showEditButton = true,
  }: RecordProps,
  ref: Ref<RecordRef>
) {
  const [formGroups, setFormGroups] = useState<Array<DynamicFormField>>();
  const [formButtonsState, setFormButtonsState] = useState<Array<DynamicFormButtonSetting>>([]);
  const [newRecordId, setNewRecordId] = useState<number>();
  const [isRecordSaved, setIsRecordSaved] = useState(true);
  const [recordTitle, setRecordTitle] = useState<{
    stringTitle: string;
    elementTitle: React.ReactNode;
  }>({
    stringTitle: constructRecordTitle('', undefined, undefined, title),
    elementTitle: constructRecordTitleElement(
      '',
      undefined,
      undefined,
      recordState === RecordSaveState.New ? title : undefined
    ),
  });
  const [showExpandButton, setShowExpandButton] = useState(true);
  const [formSettingState, setFormSettingState] = useState<ExtendedDynamicFormSettings>();
  const [recordDataState, setRecordDataState] = useState<RecordStructure>();
  const [recordSaveState, setRecordSaveState] = useState<RecordSaveState>(recordState);
  const [newRecordOldId, setNewRecordOldId] = useState<string>('');
  const recordDataOriginal = recordDataState?.original;
  const recordDataFlat = recordDataState?.flat;

  // Additional ref needed due to onPostSubmit creating a closure which cannot be updated
  const [panelContextProps, setPanelContextProps] = useState<ActiveRecordContextProps>();
  const panelContextPropsRef = useRef<ActiveRecordContextProps | undefined>(undefined);

  const { activeRecordReference } = useActiveRecordRef();
  const closeOnModalSaveRef = useRef(false);
  const lastEditedFieldId = useRef<OdinKey | undefined>(undefined);
  const currentWorkflowRef = useRef<ExtendedDynamicFormWorkflowStep | undefined>(undefined);
  const dynamicFormReference = useRef<DynamicFormRef>(null);
  const dynamicFormId = useRef(v4());
  const destroySubject = useRef(new Subject<void>());
  const saveDataSubject = useRef<Subject<RecordResult> | undefined>(undefined);
  const saveModalReference = useRef<DialogRef>(null);

  const nextWorkflowStepModalReference = useRef<WorkflowStepModalRef>(null);
  const conflictsModalRef = useRef<ConflictsModalRef>(null);
  const dataGridRef = useRef<DataGridRef | undefined>(undefined);
  const recordRef = useRef<RecordRef | undefined>(undefined);
  const existingRecordWasSavedProgressedOrRefreshedRef = useRef(false);
  const nextWorkflowStepRef = useRef<ExtendedDynamicFormNextWorkflowStep | undefined>(undefined);
  const hasWorkflowStepButtonBeenClickedRef = useRef(false);
  const isBackgroundSaveEnabled = useIsMetadataKey(BACKGROUND_SAVE_KEY, true);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const {
    analysisResult,
    isError,
    isLoading: isLoadingAnalysis,
    timestamp: analysisTimestamp,
    isOutdated: isAnalysisOutdated,
    setIsOutdated: setIsAnalysisOutdated,
    showAnalyzeIcon,
    startAIAnalysis,
  } = useAnalysisBackgroundJobHandler();
  const analysisResultRef = useRef(analysisResult);
  analysisResultRef.current = analysisResult;

  const { viewId: moduleViewId } = useParams();
  const { profileData: { user: userData } = {}, isLoadingUser } = useProfileData();
  const isSuperUser = userData?.superUser;
  const log = useApiLogger();

  const { notifyDirty, notifySaveSucceeded, notifySaveFailed } = useDynamicFormNotifier(dynamicFormId.current);
  const { showPerformingSaveToast, hidePerformingSaveToast } = usePerformingSaveToast({
    shouldFreezeActiveRecord: true,
    disabled: panelContextProps?.batchSaveInProgress,
  });

  const { data: featureTourData } = useGetFeatureToursQuery();
  const { workflowSteps, isFetchingWorkflowSteps } = useFetchWorkflowSteps(formId);

  const [getRecordById] = useLazyGetRecordByIdQuery();

  const { data: formSettingsData, isFetching: isFormSettingsDataFetching } = useFormSettingsQuery(formId ?? 0, {
    skip: formId === undefined || formId <= 0,
  });

  const {
    data: singleRecordData,
    isFetching: isRecordFetching,
    isError: hasRecordDataError,
    error: recordDataError,
  } = useGetRecord(formId, isRecordSaved, recordId, newRecordId);

  const { data: defaultRecordData, isFetching: isDefaultRecordDataFetching } = useGetDefaultRecordValuesQuery(
    {
      keyTypeId: true,
      id: formId,
    },
    {
      skip:
        formId === undefined ||
        recordId !== undefined ||
        formId <= 0 ||
        (isLinkedRecord && !isTextualQuestionnaireLinkedRecords),
      refetchOnMountOrArgChange: true,
    }
  );

  const { data: defaultLinkedRecordData } = useGetRecordLinkFieldsDefaultValuesQuery(
    {
      recordId: parentLinkedRecordId ?? 0,
      fieldId: parentLinkedFieldId ?? 0,
    },
    {
      skip:
        !parentLinkedFieldId ||
        !formId ||
        formId <= 0 ||
        !parentLinkedRecordId ||
        isTextualQuestionnaireLinkedRecords ||
        !isLinkedRecord,
    }
  );

  const { data: formAccessControl, isFetching: isFormAccessControlFetching } = useFormAccessControlQuery(formId, {
    skip: formId === undefined || formId <= 0,
  });

  const {
    recordPanelReadOnlyMode,
    canEditRecord,
    formPermissions,
    isFormPermissionsLoading,
    recordPermissions,
    isRecordPermissionLoading,
    currentRecordPermissions,
    onRecordsEdit,
  } = useRecordPermissionsAndMode(recordId ?? newRecordId, formId, openInEditMode);

  const {
    fieldVisibilitiesPromise,
    sectionVisibilitiesPromise,
    isLoading: isFieldAndSectionVisibilitiesLoading,
  } = useRecordFieldVisibilities({
    recordSaveState,
    singleRecordData,
    defaultRecordData,
    defaultLinkedRecordData,
  });

  useImperativeHandle(ref, () => ({
    scrollFieldIntoView: (fieldId: string) => dynamicFormReference.current?.scrollFieldIntoView(fieldId),
    refreshRecord: onRefreshRecord,
    recordId: recordId,
    openerRecordId: openerRecordId,
  }));

  const updateDataGridOnRecordUpdate = () => {
    dataGridRef.current?.api.data.getDataOverwritePageCache();
    dispatch(recordApi.util.invalidateTags(['TotalRecordCount']));
    existingRecordWasSavedProgressedOrRefreshedRef.current = false;
  };

  const showErrorNotification = (errorMessage: string) => {
    if (!panelContextPropsRef.current?.batchSaveInProgress) {
      showError(errorMessage, {
        duration: Infinity,
        showCloseIcon: true,
        toasterId: isLinkedRecord ? createLinkedRecordToasterId : 'default',
      });
    }
    notifySaveFailed(dynamicFormId.current);
  };

  const showSaveNotificationAndCloseRecord = (id?: string) => {
    if (!panelContextPropsRef.current?.batchSaveInProgress && !isSimpleView) {
      showSuccess(t('record-saved'), {
        id: id ?? String(singleRecordData?.original?.id ?? recordId),
        toasterId: isLinkedRecord ? createLinkedRecordToasterId : 'default',
      });
    }
    notifySaveSucceeded(dynamicFormId.current);

    if (
      nextWorkflowStepRef.current?.submissionBehaviour === 'NAVIGATE_TO_URL' &&
      nextWorkflowStepRef.current.navigationUrl
    ) {
      const url = nextWorkflowStepRef.current.navigationUrl;
      if (isSimpleView) {
        showSuccess(t('sign-in-successful'), {
          id: id ?? String(singleRecordData?.original?.id ?? recordId),
          toasterId: isLinkedRecord ? createLinkedRecordToasterId : 'default',
        });
      } else {
        showInfo(t('redirect-to-url', { url }), { id: id ?? String(singleRecordData?.original?.id ?? recordId) });
      }
      setTimeout(() => window.open(url, '_self'), 3000);
    } else if (closeOnModalSaveRef.current || nextWorkflowStepRef.current?.closeOnSave) {
      // immediattely close record if applicable (MYOSH-5368)
      if (viewId === moduleViewId || window.location.href.includes(String(viewId))) {
        updateDataGridOnRecordUpdate();
      }
      closeAndRefresh();
    }
  };

  const onSaveButtonClick = () => {
    if (recordSaveState === RecordSaveState.Update && !dynamicFormReference.current?.formIsDirty()) {
      showSaveNotificationAndCloseRecord();
    } else {
      dynamicFormReference.current?.submitForm(true);
    }
  };

  const saveRecordCallback = useCallback(() => {
    dynamicFormReference.current?.submitForm(true);
    return saveDataSubject.current?.asObservable();
  }, []);

  const getRecordData = useCallback(() => dynamicFormReference.current?.getData(), []);

  const setFieldValue = useCallback(
    (name: string, value: unknown) => dynamicFormReference.current?.setFieldValue(name, value),
    []
  );

  const updateLastChangeValueCallback = useCallback((lastChange: string) => {
    setRecordDataState((recordDataState) => {
      if (recordDataState) {
        return {
          original: { ...recordDataState.original, lastChange },
          flat: { ...recordDataState.flat, lastChange },
        };
      }
      return recordDataState;
    });
  }, []);

  const updateRecordDataToIncludeAttachments = useCallback(
    ({ confirmations, questionnaireConfirmations }: AttachmentsPayloadProps) => {
      setRecordDataState((recordDataState) => {
        if (recordDataState) {
          const addAttachmentConfirmations = (current: AttachmentsPayloadProps) => {
            const existingQuestionnaireAttachments = current.questionnaireConfirmations || [];
            const newQuestionnaireAttachments = questionnaireConfirmations || [];

            const incomingIds = newQuestionnaireAttachments.map((q) => q.questionId);
            const previousQuestionsAttachments = existingQuestionnaireAttachments.filter(
              (q) => !incomingIds.includes(q.questionId)
            );

            return {
              ...current,
              ...(confirmations !== undefined && { confirmations }),
              ...(questionnaireConfirmations !== undefined && {
                questionnaireConfirmations: [...previousQuestionsAttachments, ...newQuestionnaireAttachments],
              }),
            };
          };

          return {
            original: {
              ...recordDataState.original,
              attachmentConfirmation: addAttachmentConfirmations(recordDataState.original.attachmentConfirmation || {}),
            },
            flat: {
              ...recordDataState.flat,
              attachmentConfirmation: addAttachmentConfirmations(recordDataState.flat.attachmentConfirmation || {}),
            },
          };
        }
        return recordDataState;
      });
    },
    []
  );

  useEffect(() => {
    if (recordSaveState !== recordState) {
      setRecordSaveState(recordState);
    }
    // Do not modify this dependency array
  }, [recordState]);

  useEffect(() => {
    panelSettings?.pipe(takeUntil(destroySubject.current)).subscribe((settings) => {
      panelContextPropsRef.current = settings;
      setPanelContextProps(settings);
    });

    onCloseActiveRecordConfirmationModalRef
      ?.subscribeToDialogResult?.()
      ?.pipe(takeUntil(destroySubject.current))
      .subscribe((result) => {
        if (result.buttonName === 'save' && dynamicFormReference.current?.formIsDirty()) {
          closeOnModalSaveRef.current = true;
          dynamicFormReference.current?.submitForm(true);
        } else if (result.buttonName === 'discard') {
          closeWithoutRefresh();
        }
      });

    getDataGridReference()
      .pipe(takeUntil(destroySubject.current))
      .subscribe((gridRef) => {
        if (gridRef) {
          dataGridRef.current = gridRef;
        }
      });

    getRecordReference()
      .pipe(takeUntil(destroySubject.current))
      .subscribe((rRef) => {
        if (rRef) {
          recordRef.current = rRef;
        }
      });

    saveDataSubject.current = new Subject<RecordResult>();

    return () => {
      destroySubject.current.next();
      destroySubject.current.complete();
      saveDataSubject.current?.complete();
    };
  }, []);

  useEffect(() => {
    if (
      Array.isArray(reverseLinkedRecords) &&
      reverseRecordLinkFormId &&
      recordSaveState === RecordSaveState.New &&
      isLinkedRecord === true &&
      formSettingState
    ) {
      const draftWorkflowStep = workflowSteps?.find((step) => step.draft === true);

      const recordData: RecordStructure = recordDataState ||
        defaultRecordData ||
        defaultLinkedRecordData || {
          original: {
            formId,
            fields: {},
            hierarchies: {},
            status: draftWorkflowStep?.label || 'Draft',
          },
          flat: {},
        };

      // use the form settings to find the reverse record link field and load the reverse record link ids into it
      // I'm not a fan of using the dynamic properties to find the correct dynamic field, but in this case we control
      // both ends of the code. In the past my dislike was because it was passing something from odin-web and using that
      // value in odin-components which would be writing odin-web specific logic in odin-components.
      const reverseRecordLinkField = findFieldRecursiveSearch(
        formSettingState.fields,
        (field) =>
          field.fieldType === 'CUSTOM' &&
          field.customFieldType === 'REVERSE_RECORD_LINK' &&
          field.dynamicProperties?.targetModuleFormId !== undefined &&
          Number(field.dynamicProperties.targetModuleFormId) === reverseRecordLinkFormId
      );
      if (reverseRecordLinkField && reverseRecordLinkField.id) {
        recordData.flat[reverseRecordLinkField.id] = reverseLinkedRecords;
        recordData.original.fields[reverseRecordLinkField.id] = reverseLinkedRecords;
        setRecordDataState(recordData);
      }
    }
  }, [
    isLinkedRecord,
    reverseLinkedRecords,
    reverseRecordLinkFormId,
    recordSaveState,
    recordDataState,
    formSettingState,
    defaultLinkedRecordData,
  ]);

  //workflow step and form buttons
  useEffect(() => {
    const isUpdate = recordSaveState === RecordSaveState.Update && (recordPermissions || isDbRecordId(recordId));

    if (workflowSteps && formSettingState && formPermissions && (recordSaveState === RecordSaveState.New || isUpdate)) {
      const initialStep = workflowSteps.find((step) => step.id === formSettingState.initialStep?.id);
      const currentStep = currentWorkflowStep(
        formPermissions,
        singleRecordData?.original?.status ?? initialStep?.label
      );
      currentWorkflowRef.current = workflowSteps.find((step) => step.id === currentStep?.id);

      if (recordSaveState === RecordSaveState.New || (currentWorkflowRef.current?.editModeByDefault && canEditRecord)) {
        onRecordsEdit();
      }

      const nextStepButtons: Array<DynamicFormButtonSetting> = [];
      if (currentWorkflowRef.current?.nextSteps) {
        for (let i = 0, length = currentWorkflowRef.current?.nextSteps.length; i < length; i++) {
          const nextStep = currentWorkflowRef.current?.nextSteps[i];
          const nextStepButton: DynamicFormButtonSetting = {
            name: nextStep.label,
            text: nextStep.buttonCaption ?? '',
            htmlType: 'button', // workflow buttons should validate (MYOSH-1565), but they can be configured to skip validation (MYOSH-2277)
            id: `${nextStep.id}_${i}`,
            type: 'primary',
            variant: 'alternative',
            hidden: nextStep.hidden,
            className: 'flex-none',
            onClick: () => handleWorkflowStepButtonClick(nextStep),
          };

          if (recordSaveState === RecordSaveState.Update && (currentStep || currentRecordPermissions)) {
            const canProgress = currentStep?.canProgress || currentRecordPermissions?.progressToNextStep || false;

            nextStepButtons.push({
              ...nextStepButton,
              hidden: !canProgress,
            });
          } else if (
            recordSaveState === RecordSaveState.New &&
            (defaultRecordData || defaultLinkedRecordData) &&
            formAccessControl &&
            userData
          ) {
            const defaultRecordFormData = (defaultRecordData || defaultLinkedRecordData) as RecordStructure;
            const canProgressRecord = workflowStepCanProgressPermission(
              formAccessControl.workflowSteps,
              defaultRecordFormData,
              currentWorkflowRef.current.id,
              userData
            );

            nextStepButtons.push({
              ...nextStepButton,
              hidden: !canProgressRecord,
            });
          }
        }
      }

      if (currentWorkflowRef.current?.hideRecordSaveButton === true) {
        setFormButtonsState([...nextStepButtons]);
      } else {
        const saveButton: DynamicFormButtonSetting = {
          name: 'save',
          text: t('save'),
          htmlType: 'button', // saving a record should not validate the form (https://myosh.atlassian.net/browse/MYOSH-1565?focusedCommentId=17471)
          type: 'text',
          onClick: onSaveButtonClick,
        };
        setFormButtonsState([...nextStepButtons, saveButton]);
      }
    }
  }, [
    workflowSteps,
    formSettingState,
    singleRecordData,
    recordSaveState,
    formPermissions,
    recordPermissions,
    formAccessControl,
    defaultRecordData,
    defaultLinkedRecordData,
    userData,
    recordId,
  ]);

  useEffect(() => {
    if (hasRecordDataError && recordDataError) {
      const errorResponse = recordDataError as GenericErrorResponse;
      if (
        errorResponse.status === 404 &&
        errorResponse.data.validation.errors?.[0].includes('DeletedRecordException')
      ) {
        showError(t('deleted-record-message'));
        dataGridRef.current?.api.data.removeRowById(Number(recordId));
        dispatch(recordApi.util.invalidateTags(['TotalRecordCount']));
        onClose(dynamicFormId.current);
      } else if (errorResponse.status === 403) {
        showError(t('record-toast-message'));
        dataGridRef.current?.api.data.removeRowById(Number(recordId));
        dispatch(recordApi.util.invalidateTags(['TotalRecordCount']));
        closeAndRefresh();
      } else {
        const queryError = recordDataError as FetchBaseQueryError;
        if (queryError.status === 'CUSTOM_ERROR' && queryError.error === 'OFFLINE_NO_DATA') {
          showInfo(t('offline-no-data', 'Unable to retrieve data while offline'), { id: 'record-offline-no-data' });
          setTimeout(() => onClose(dynamicFormId.current), 1000);
        }
      }
    } else if (
      singleRecordData &&
      !isRecordFetching &&
      recordSaveState === RecordSaveState.Update &&
      !hasRecordDataError
    ) {
      const _singleRecordData = cloneDeep(singleRecordData);
      setRecordTitle({
        stringTitle: constructRecordTitle(
          _singleRecordData.original.formName,
          _singleRecordData.original.docNo,
          _singleRecordData.original.status,
          _singleRecordData.original.title
        ),
        elementTitle: constructRecordTitleElement(
          _singleRecordData.original.formName,
          _singleRecordData.original.docNo,
          _singleRecordData.original.status,
          undefined
        ),
      });
      if (dynamicFormReference.current?.formIsDirty() || existingRecordWasSavedProgressedOrRefreshedRef.current) {
        dynamicFormReference.current?.resetFormState({ ..._singleRecordData.flat });
      }

      setRecordDataState(_singleRecordData);
    } else if (
      singleRecordData &&
      !isRecordFetching &&
      !hasRecordDataError &&
      !isRecordPermissionLoading &&
      recordSaveState === RecordSaveState.New &&
      !isLinkedRecord
    ) {
      setRecordTitle({
        stringTitle: constructRecordTitle(
          singleRecordData.original.formName,
          singleRecordData.original.docNo,
          singleRecordData.original.status,
          singleRecordData.original.title
        ),
        elementTitle: constructRecordTitleElement(
          singleRecordData.original.formName,
          singleRecordData.original.docNo,
          singleRecordData.original.status,
          undefined
        ),
      });
      const details = (
        <RecordTitle
          title={recordTitle.elementTitle}
          date={singleRecordData.original.creationDate ? new Date(singleRecordData.original.creationDate) : undefined}
        />
      );

      activeRecordReference.current?.updateActiveRecord(
        newRecordOldId,
        String(singleRecordData.original.id),
        moduleIcon,
        details,
        false,
        {
          title,
          recordId: singleRecordData.original.id,
          viewId,
          formId,
          formName: formSettingState?.caption,
          docNo: singleRecordData.original.docNo,
          status: singleRecordData.original.status,
          vikingLinkUrl: singleRecordData.original.vikingLinkUrl,
          displayText: singleRecordData.original.displayText,
        }
      );
    } else if (
      !singleRecordData &&
      !isRecordFetching &&
      recordSaveState === RecordSaveState.New &&
      (((!isLinkedRecord || (isLinkedRecord && isTextualQuestionnaireLinkedRecords)) && defaultRecordData) ||
        (isLinkedRecord && !isTextualQuestionnaireLinkedRecords && defaultLinkedRecordData))
    ) {
      const defaultRecordFormData = (defaultRecordData || defaultLinkedRecordData) as RecordStructure;
      const clonedDefaultRecordData = cloneDeep(defaultRecordFormData);
      const defaultDataKeys = Object.keys(defaultRecordFormData.flat);
      for (let i = 0; i < defaultDataKeys.length; i++) {
        const field = findFieldRecursiveSearch(
          formSettingsData?.fields ?? [],
          (field) => field.id.toString() === defaultDataKeys[i]
        );

        if (field?.fieldType === 'TIMEFIELD') {
          // The default value for a TIMEFIELD is retuned in this format: T02:45:03.000+08 or 02:45
          const timeFieldValue = defaultRecordFormData.flat[defaultDataKeys[i]] as string;
          clonedDefaultRecordData.flat[defaultDataKeys[i]] = timeFieldValue.startsWith('T')
            ? timeFieldValue.slice(1, 6)
            : timeFieldValue;
        }
      }

      setRecordDataState(clonedDefaultRecordData);
    }
  }, [
    defaultRecordData,
    defaultLinkedRecordData,
    singleRecordData,
    isRecordFetching,
    recordPermissions,
    recordDataError,
    recordSaveState,
  ]);

  useEffect(() => {
    if (
      formSettingsData &&
      !isFormSettingsDataFetching &&
      workflowSteps &&
      !isFetchingWorkflowSteps &&
      formPermissions &&
      !isFormPermissionsLoading &&
      userData &&
      !isLoadingUser &&
      formAccessControl &&
      !isFormAccessControlFetching &&
      (singleRecordData || recordSaveState === RecordSaveState.New) &&
      !isRecordFetching &&
      fieldVisibilitiesPromise &&
      sectionVisibilitiesPromise
    ) {
      Promise.allSettled([fieldVisibilitiesPromise, sectionVisibilitiesPromise]).then(
        ([fieldVisibilitiesResult, sectionVisibilitiesResult]) => {
          const clonedFormSettingsData = cloneDeep(formSettingsData);

          // Preserve existing aiAnalysis field if it exists
          const existingAiAnalysis = formSettingState?.fields.find((field) => field.id === 'aiAnalysis');

          const initialStep = workflowSteps.find((step) => step.id === formSettingsData.initialStep?.id);
          const currentStep = currentWorkflowStep(
            formPermissions,
            singleRecordData?.original?.status ?? initialStep?.label
          );
          const extractedSummaryFormFields = extractFormGroupFieldsByFieldType(
            formSettingsData?.fields,
            summaryRecordLinkFieldTypes as CombinedFieldType[]
          );
          const pathname = window.location.pathname;
          const oldId = pathname.substring(pathname.lastIndexOf('/') + 1);
          setNewRecordOldId(oldId);

          clonedFormSettingsData.customFieldComponents = {
            HIERARCHY: HfHierarchyField,
            PERSONFIELD: HfPersonField,
            TRAINING: HfTrainingField,
            MULTISELECTPERSONFIELD: HfMultiselectPersonField,
            ATTACHMENTS: HfAttachmentsField,
            TEXTUAL_QUESTIONNAIRE: HfQuestionnaire,
            NUMERIC_QUESTIONNAIRE: HfQuestionnaire,
            RISKRATING: HfRiskRating,
            RECORDLINK: HfLinkedRecords,
            REVERSE_RECORD_LINK: HfReverseLinkedRecords,
            USER_RECORDLINK: HfUserRecordLink,
            READERS: HfReadersField,
            SUMMARY_RECORD_LINK: HfSummaryRecordLink,
            VERSION: HfVersionField,
            INVITATION: HfInvitationField,
            EMAIL_THIS_BUTTON: HfEmailThisButton,
            INVITE_BUTTON: HfInviteButtonField,
            PDF_EXPORT: HfExportButtonField,
            WORD_EXPORT: HfExportButtonField,
            CLONERECORD: HfCloneRecordButtonField,
            LINK: HfLinkField,
            RECORD_OPTIONGROUP: HfOptionGroup,
            RECORD_MULTISELECTFIELD: HfComboboxMultiselect,
            RECORD_COMBOBOX: HfCombobox,
            RECORD_MULTISELECTCHECKBOX: HfMultiselectCheckbox,
            RECORD_TWINCOLUMNSELECT: HfTwinColumnSelect,
            AI_FIELD: AIField,
            AI_ANALYSIS: SingleRecordAiAnalysis,
          };

          clonedFormSettingsData.fieldValueConverters = {
            CHECKBOX: (value) => {
              const convertedValue = forceAssert<CheckboxChangeEventType>(value);
              return convertedValue.checked;
            },
            DATEFIELD: (value) => {
              if (value !== undefined) {
                return value;
              } else {
                return null;
              }
            },
            DATEFIELD_COMPETENCY: (value) => {
              if (value !== undefined) {
                return value;
              } else {
                return null;
              }
            },
          };

          const isHierarchiesHidden =
            (recordPanelReadOnlyMode && clonedFormSettingsData.hierarchySettings?.hiddenInReadMode) ||
            !clonedFormSettingsData.hierarchySettings?.hierarchyTypes?.length;

          clonedFormSettingsData.fields.unshift(
            {
              id: 'aiAnalysis',
              caption: t('ai-analysis'),
              fieldName: 'aiAnalysis',
              fieldType: 'CUSTOM',
              position: 0,
              required: DynamicFormRequiredType.False,
              customFieldType: 'AI_ANALYSIS',
              wrapInGroup: true,
              customGroupTitle: t('ai-analysis'),
              startOpen: true,
              hidden: true,
              permissions: {
                read: true,
              },
              ...existingAiAnalysis,
            },
            {
              id: 'hierarchyValues',
              caption: t('hierarchy'),
              fieldName: 'hierarchyValues',
              fieldType: 'CUSTOM',
              position: 1,
              dynamicProperties: {
                hierarchyTypes: clonedFormSettingsData?.hierarchySettings?.hierarchyTypes,
                isExternal: false,
              },
              required: DynamicFormRequiredType.True,
              customFieldType: 'HIERARCHY',
              wrapInGroup: true,
              customGroupTitle: t('hierarchy'),
              hidden: isHierarchiesHidden,
              startOpen: clonedFormSettingsData.hierarchySettings?.isExpanded,
              validationFunc: validateByFieldType(t('hierarchy'), 'HIERARCHY', {
                hierarchyTypes: clonedFormSettingsData?.hierarchySettings?.hierarchyTypes,
                isExternal: false,
                isNewRecord: recordSaveState === RecordSaveState.New,
              }),
            }
          );

          clonedFormSettingsData.fields.push({
            id: 'version',
            caption: t('version'),
            fieldName: 'version',
            fieldType: 'CUSTOM',
            position: clonedFormSettingsData.fields.length,
            customFieldType: 'VERSION',
            dynamicProperties: {
              version: singleRecordData?.flat['version'],
              locationState: {
                moduleIcon,
                viewId,
                formId,
                formName: clonedFormSettingsData.caption,
                vikingLinkUrl,
              },
            },
            wrapInGroup: true,
            customGroupTitle: t('version'),
            startOpen: true,
            hidden: clonedFormSettingsData.versionType === 0, //versionType is 0 when versioning is not enabled for the form https://myosh.atlassian.net/browse/MYOSH-7726
          });

          clonedFormSettingsData.fields.push({
            id: 'invitation',
            caption: t('invitations'),
            fieldName: 'invitation',
            fieldType: 'CUSTOM',
            position: clonedFormSettingsData.fields.length,
            customFieldType: 'INVITATION',
            wrapInGroup: true,
            customGroupTitle: t('invitations'),
            startOpen: true,
            dynamicProperties: {
              saveRecord: saveRecordCallback,
              updateLastChangeValue: updateLastChangeValueCallback,
              setFormButtonsState: setFormButtonsState,
            },
            hidden: !clonedFormSettingsData.enableExternalUserSignUp,
          });

          clonedFormSettingsData.fields.push({
            id: 'attachments', // Odin specific ID
            caption: t('attachments'),
            fieldName: 'attachments',
            fieldType: 'CUSTOM',
            position: clonedFormSettingsData.fields.length,
            customFieldType: 'ATTACHMENTS',
            dynamicProperties: {
              files: singleRecordData?.flat?.files ?? [],
              images: singleRecordData?.flat?.images ?? [],
              isAccordion: true,
              setFormButtonsState: setFormButtonsState,
              getRecordData: getRecordData,
              updateRecordDataToIncludeAttachments: updateRecordDataToIncludeAttachments,
            },
            wrapInGroup: true,
            customGroupTitle: t('attachments'),
            startOpen: true,
          });

          const currentWorkflowStepRolesPermissions = workflowSteps?.find(
            (step) => step.label === singleRecordData?.original.status
          )?.rolesPermissions;

          //modifies components into having a fieldType: CUSTOM and attaches permissions
          clonedFormSettingsData.fields = clonedFormSettingsData.fields.map((field) => {
            const personFieldPermissions = getFormGroupPersonFieldPermissions(
              String(field.id),
              currentStep?.id,
              currentWorkflowStepRolesPermissions,
              clonedFormSettingsData.accessRights?.[field.id]
            );

            //attaches permissions to view or edit a form group
            const groupFieldPermissions = formGroupPermissions(
              currentStep?.sectionPermissions,
              personFieldPermissions,
              singleRecordData?.flat?.fields as Record<string, unknown>,
              customPropertyOfFormGroups(field.fieldName),
              field.id,
              userData.id
            );

            field = {
              ...field,
              hidden: field.hidden || groupFieldPermissions.read === false,
              isVisibleForced:
                sectionVisibilitiesResult.status === 'fulfilled'
                  ? sectionVisibilitiesResult.value[Number(field.id)]
                  : undefined,
              permissions: groupFieldPermissions,
            };

            if (field.fieldType === 'GROUP' && field.fields) {
              field.fields = field.fields.map((fieldItem) => {
                fieldItem.permissions = groupFieldPermissions;
                fieldItem.isVisibleForced =
                  fieldVisibilitiesResult.status === 'fulfilled'
                    ? fieldVisibilitiesResult.value[Number(fieldItem.id)]
                    : undefined;
                fieldItem.hidden = fieldItem.hidden || groupFieldPermissions.read === false;

                const fieldType: CombinedFieldType = fieldItem.fieldType as CombinedFieldType;
                if (
                  [
                    'PERSONFIELD',
                    'TRAINING',
                    'MULTISELECTPERSONFIELD',
                    'RISKRATING',
                    'RECORDLINK',
                    'TEXTUAL_QUESTIONNAIRE',
                    'NUMERIC_QUESTIONNAIRE',
                    'REVERSE_RECORD_LINK',
                    'USER_RECORDLINK',
                    'READERS',
                    'SUMMARY_RECORD_LINK',
                    'INVITE_BUTTON',
                    'EMAIL_THIS_BUTTON',
                    'PDF_EXPORT',
                    'WORD_EXPORT',
                    'CLONERECORD',
                    'LINK',
                    'RECORD_OPTIONGROUP',
                    'RECORD_COMBOBOX',
                    'RECORD_MULTISELECTFIELD',
                    'RECORD_MULTISELECTCHECKBOX',
                    'RECORD_TWINCOLUMNSELECT',
                    'AI_FIELD',
                    'AI_ANALYSIS',
                  ].includes(fieldType)
                ) {
                  return {
                    ...fieldItem,
                    customFieldType: fieldType,
                    fieldType: 'CUSTOM',
                    validationFunc: validateByFieldType(fieldItem.caption, fieldType, fieldItem?.dynamicProperties),
                    dynamicProperties: {
                      ...fieldItem.dynamicProperties,
                      saveRecord: saveRecordCallback,
                      recordSaveState,
                      summaryRecordLinkFormFields:
                        fieldType === 'SUMMARY_RECORD_LINK' ? extractedSummaryFormFields : [],
                      resetField: (name: string, value?: unknown) =>
                        dynamicFormReference.current?.resetFormField(name, value),
                      cloneRecordBackgroundJobItemText: singleRecordData
                        ? `${singleRecordData.flat.moduleName} - ${singleRecordData.flat?.docNo}`
                        : clonedFormSettingsData.caption,
                      setFieldValue: setFieldValue,
                      updateLastChangeValue: updateLastChangeValueCallback,
                      setFormButtonsState: setFormButtonsState,
                      getRecordData: getRecordData,
                      updateRecordDataToIncludeAttachments: updateRecordDataToIncludeAttachments,
                      formIsDirty: () => dynamicFormReference.current?.formIsDirty(),
                    },
                  };
                }

                if (fieldItem.fieldType === 'HISTORY') {
                  return {
                    ...fieldItem,
                    dynamicProperties: {
                      ...fieldItem.dynamicProperties,
                      fullName: userData.fullName ?? t('user'),
                    },
                  };
                }

                return fieldItem;
              });
            }
            return field;
          });

          clonedFormSettingsData.globalProperties = {
            isPreview,
            recordId: singleRecordData?.flat.id ?? recordId,
            formId,
            filterLocation: DataSearchLocation.Api,
            hierarchySettings: clonedFormSettingsData.hierarchySettings,
            shouldUseCreatePortal: true,
          };

          clonedFormSettingsData.userGroups = isSuperUser
            ? [
                {
                  id: -1,
                  caption: t('super-user'),
                },
              ]
            : userData.groups?.map((group) => {
                return {
                  id: group.id,
                  caption: group.caption.translations[0].value,
                };
              });

          // build workflow step visibility conditions
          const activeWorkflowStep = workflowSteps?.find((step) => step.id === currentStep?.id);
          const workflowStepVisibilityConditions = buildWorkflowStepVisibilityConditions(activeWorkflowStep);

          // ensure access control conditions are also applied dynamically
          if (formAccessControl && userData && activeWorkflowStep) {
            addAccessControlConditionsAsVisibilityConditions(
              workflowStepVisibilityConditions,
              formAccessControl.workflowSteps,
              activeWorkflowStep,
              userData.id
            );
          }

          clonedFormSettingsData.workflowStepVisibilityConditions = workflowStepVisibilityConditions;

          setFormSettingState(clonedFormSettingsData);

          if (clonedFormSettingsData.fields.length > 0) {
            setVisibleGroups(clonedFormSettingsData.fields, isHierarchiesHidden);
          }
        }
      );
    }
  }, [
    formSettingsData,
    isFormSettingsDataFetching,
    workflowSteps,
    isFetchingWorkflowSteps,
    formPermissions,
    isFormPermissionsLoading,
    userData,
    isLoadingUser,
    singleRecordData,
    isRecordFetching,
    formAccessControl,
    isFormAccessControlFetching,
    recordPanelReadOnlyMode,
    recordSaveState,
    fieldVisibilitiesPromise,
    sectionVisibilitiesPromise,
  ]);

  const tagCreator = useTestTag('record');

  const setVisibleGroups = (fields: Array<DynamicFormField>, isHierarchiesHidden = false) => {
    const formGroups: DynamicFormField[] = [];
    // currently in the API all top level fields are groups, however, it is possible in the future that fields could
    // be at the top level without being in a group (dynamic form supports this already) so to make this code less
    // brittle I am filtering out the top level non-group fields
    const groupFields = fields.filter(
      (field) =>
        (field.fieldType === 'GROUP' && field.permissions?.read === true) ||
        (field.fieldType === 'CUSTOM' && field.wrapInGroup === true && field.permissions?.read === true)
    );
    for (let i = 0; i < groupFields.length; i++) {
      const recordGroup = groupFields[i];
      if (recordGroup.id === 'hierarchyValues' && !isHierarchiesHidden) {
        formGroups.push(recordGroup);
      } else if (!recordGroup.hidden && recordGroup.id !== 'hierarchyValues') {
        formGroups.push(recordGroup);
      }
    }

    setFormGroups(formGroups);
  };

  const dataRetrieval = useMemo<OdinDataRetrieval & OdinDataUpdate<JsonDataWrapper>>(() => {
    return {
      getData: async (subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
        await getRecordFieldOptions(formId, subscriber, options);
      },
      saveData: (
        data: JsonDataItem | JsonData,
        returnResult: OdinDataSender<JsonDataWrapper>,
        options?: OdinDataRetrievalOptions
      ) => {
        if (!isArray(data) && options?.customFieldType === 'ADDNEWMODAL' && options.fieldId) {
          returnResult.sendData({ data: [data], requestId: options.requestId });
        }
      },
    };
  }, [formId]);

  const onRefreshRecord = useCallback(() => {
    // When creating a new linked record and the modal closes it doesn't have a recordId
    if (recordId) {
      invalidateRecordSpecificTags(recordId);
      invalidateSharedTags();
      existingRecordWasSavedProgressedOrRefreshedRef.current = true;
    }
  }, [recordId]);

  const closeWithoutRefresh = useCallback(() => {
    if (window.ReactNativeWebView && !isLinkedRecord) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'CLOSE_WEBVIEW', viewId, formId, recordId }));
    }
    onClose(dynamicFormId.current);
  }, [onClose, isLinkedRecord, viewId, formId, recordId]);

  const closeAndRefresh = useCallback(() => {
    closeWithoutRefresh();
    onRefreshRecord();
  }, [closeWithoutRefresh, onRefreshRecord]);

  const onSaveConfirmationCancel = useCallback(
    (returnValue?: string) => {
      if (returnValue !== 'save') {
        // User chose to discard - close without cache invalidation
        closeWithoutRefresh();
      }
    },
    [closeWithoutRefresh]
  );

  const handleWorkflowStepButtonClick = useCallback((nextStep: ExtendedDynamicFormNextWorkflowStep) => {
    if (nextStep?.promptBeforeAdvance) {
      nextWorkflowStepModalReference.current?.show(nextStep).then((result) => {
        if (result && result.id === nextStep?.id) {
          workflowStepButtonClick(nextStep);
        }
      });
    } else {
      workflowStepButtonClick(nextStep);
    }
  }, []);

  const workflowStepButtonClick = useCallback((nextStep: ExtendedDynamicFormNextWorkflowStep) => {
    nextWorkflowStepRef.current = nextStep;
    hasWorkflowStepButtonBeenClickedRef.current = true;
    const skipValidation = nextStep.skipMandatoryFieldValidation === true;
    dynamicFormReference.current?.submitForm(skipValidation);
  }, []);

  const onSaveConfirm = () => {
    if (recordDataOriginal) {
      closeOnModalSaveRef.current = true;
      dynamicFormReference.current?.submitForm(true);
    } else {
      closeAndRefresh();
    }
  };

  const onRecordClose = () => {
    if (!dynamicFormReference.current?.formIsDirty()) {
      // Record is clean - close without cache invalidation
      closeWithoutRefresh();
    } else {
      // Record has changes - show save confirmation modal
      saveModalReference.current?.show();
    }
  };

  const onGroupItemClicked = (groupId: string, fieldType: string) => {
    if (fieldType === 'GROUP' || fieldType === 'CUSTOM') {
      dynamicFormReference.current?.moveGroupToTop(groupId);
    }
  };

  // Handles data grid updates when saving, progressing through the workflow steps or refreshing an existing record
  // if the record is from the module you're on
  useEffect(() => {
    if (hasRecordDataError) {
      existingRecordWasSavedProgressedOrRefreshedRef.current = false;
    } else if (
      (viewId === moduleViewId || window.location.href.includes(String(viewId))) &&
      recordSaveState === RecordSaveState.Update &&
      existingRecordWasSavedProgressedOrRefreshedRef.current &&
      !isRecordFetching &&
      singleRecordData
    ) {
      updateDataGridOnRecordUpdate();
    }
  }, [singleRecordData, recordSaveState, isRecordFetching, hasRecordDataError]);

  const _onUpdateRecordSuccess = useCallback(() => {
    setIsRecordSaved(true);
    dispatch(fileApi.util.invalidateTags([{ type: 'File', id: `${recordId}-LIST` } as const, 'QuestionnaireFile']));
    showSaveNotificationAndCloseRecord();
    // scroll to last edited field, after timeout, to ensure the form has rendered
    setTimeout(
      () => dynamicFormReference.current?.scrollFieldIntoView((lastEditedFieldId.current || '') as string),
      3000
    );

    // Set flag to indicate cache invalidation is needed after successful save
    existingRecordWasSavedProgressedOrRefreshedRef.current = true;
  }, [showSaveNotificationAndCloseRecord]);

  const saveRecord = useCallback(
    (saveData: RecordResult, syncDetails?: SyncDetails) => {
      return mutateRecord(saveData, {
        recordId, // passed to ensure offline records are saved correctly
        isBackgroundSaveEnabled,
        recordSaveState,
        destroySubject: destroySubject.current,
        formCaption: formSettingState?.caption,
        docNo: singleRecordData?.flat?.docNo as string,
        parentLinkedRecordId,
        stepAdvanced: hasWorkflowStepButtonBeenClickedRef.current,
        syncDetails,
      }).then((result) => {
        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              type: 'RECORD_SAVED',
              viewId,
              formId,
              recordId: result,
            })
          );
        }
        return result;
      });
    },
    [
      isBackgroundSaveEnabled,
      recordSaveState,
      formSettingState?.caption,
      singleRecordData?.flat?.docNo,
      parentLinkedRecordId,
      viewId,
      formId,
      recordId,
    ]
  );

  const _saveConflictResolvedRecord = useCallback(
    (record: RecordResult) => {
      saveRecord(record)
        .then(() => {
          hidePerformingSaveToast();
          _onUpdateRecordSuccess();
        })
        .catch((error) => {
          hidePerformingSaveToast();
          showErrorNotification(errorMessage(error));
          log('Failed to save conflict resolved record', { formId, error });
        });
    },
    [saveRecord, _onUpdateRecordSuccess, showErrorNotification, hidePerformingSaveToast]
  );

  const _resolveRecordMergeConflicts = useCallback(
    async (initialServerRecord: RecordResult, localRecord: RecordResult) => {
      try {
        // // resolve the record merge conflicts, explicitly avoid query cache
        const result = await getRecordById({ id: recordId as string, keyTypeId: true, cache: false }).unwrap();
        const serverRecord = result.original;
        const conflicts = findMergeConflicts(
          initialServerRecord,
          localRecord,
          serverRecord,
          dynamicFormReference.current?.formDirtyFields() || {},
          formSettingState as ExtendedDynamicFormSettings
        );

        if (conflicts.length > 0) {
          hidePerformingSaveToast();

          if (conflictsModalRef.current) {
            // await on value to avoid stale closures issues
            const conflictResolution: ConflictResolution = await firstValueFrom(
              conflictsModalRef.current.show(conflicts).pipe(take(1), takeUntil(destroySubject.current))
            );
            const { fields } = conflictResolution;
            if (fields && fields.length > 0) {
              // write resolved values
              const fieldsLength = fields.length;
              for (let i = 0; i < fieldsLength; i++) {
                if ('hierarchyValues' === fields[i].id) {
                  localRecord.hierarchyValues = fields[i].value as Record<string, HierarchyDropdownValue>;
                } else {
                  localRecord.fields[fields[i].id] = fields[i].value;
                }
              }

              // consolidated files/images
              localRecord.files = [...(serverRecord.files ?? [])];
              localRecord.images = [...(serverRecord.images ?? [])];

              // update lastChange and status value to allow save
              localRecord.lastChange = serverRecord.lastChange;
              localRecord.status = serverRecord.status;

              // save the record
              showPerformingSaveToast();
              _saveConflictResolvedRecord(localRecord);
            }
          }
        } else {
          // update lastChange and status value to allow save
          localRecord.lastChange = serverRecord.lastChange;
          localRecord.status = serverRecord.status;

          _saveConflictResolvedRecord(localRecord);
        }
      } catch (exception) {
        hidePerformingSaveToast();
        showErrorNotification(t('record-save-failed'));
        log('Conflict resolution failed', { formId, exception });
      }
    },
    [getRecordById, formSettingState, _saveConflictResolvedRecord, showErrorNotification]
  );

  const saveNewRecord = useCallback(
    (record: RecordResult, sender: OdinDataSender<OdinKey | undefined>, syncDetails?: SyncDetails) => {
      saveRecord(record, syncDetails)
        .then((recordId) => {
          sender.sendData(recordId);

          saveDataSubject.current?.next({
            ...record,
            id: recordId,
          });

          dataGridRef?.current?.api.data.getDataOverwritePageCache();
        })
        .catch((error) => {
          hidePerformingSaveToast();
          showErrorNotification(errorMessage(error));
          log('Failed to save new record', { formId, error });
        });
    },
    [saveRecord, showErrorNotification, hidePerformingSaveToast]
  );

  const onError = useCallback(() => {
    if (!panelContextPropsRef.current?.batchSaveInProgress) {
      showWarning(t('record-validation-message'));
    }
    notifySaveFailed(dynamicFormId.current);
    setShowExpandButton(false);
    nextWorkflowStepRef.current = undefined; // clear nextWorkflowStepRef on validation error
    hasWorkflowStepButtonBeenClickedRef.current = false;
  }, []);

  const onGetPreSubmitData = () => {
    // When saving a linked record, we need to show the notification in the linked records notification area
    showPerformingSaveToast(isLinkedRecord ? createLinkedRecordToasterId : 'default');
  };

  const onPreSubmit = useCallback<PreSubmitHandler>((preSubmitData, formData) => {
    let _removedFiles: Array<string> = [];

    preSubmitData
      .filter((item) => item !== undefined)
      .forEach((item) => {
        if (item.removed) {
          const removed = item.removed as AttachmentsRemove;
          if (removed.questionId) {
            // attachment field in questionnaire
            const questionValue = (formData[removed.id as number] as QuestionnaireFieldValue).value;
            questionValue?.forEach((value) => {
              if (value.questionId === removed.questionId) {
                delete value?.attachments; // remove the 'validation' only attachment list
              }
            });
          }

          _removedFiles = [..._removedFiles, ...removed.deletedAttachmentIds];
        }
      });

    if (_removedFiles.length > 0) {
      const _removed = uniq(_removedFiles);
      if (formData.files) {
        formData.files = (formData.files as Array<string>).filter((fileId) => _removed.indexOf(fileId) === -1);
      }
      if (formData.images) {
        formData.images = (formData.images as Array<string>).filter((imageId) => _removed.indexOf(imageId) === -1);
      }

      // include the removed files/images as 'deletedFiles' in the payload (https://myosh.atlassian.net/browse/MYOSH-6113?focusedCommentId=53410)
      formData.deletedFiles = [..._removed];
    }

    // cleanup properties that cause issues with the API
    cleanupRecordData(formData);

    return formData;
  }, []);

  const onFormSubmit = useCallback(
    (data: JsonDataItem, sender: OdinDataSender<OdinKey | undefined>) => {
      const transformContext = {
        fields: formSettingState?.fields || [],
        isDbRecord: isDbRecordId(recordId),
      };

      if (recordDataOriginal) {
        const initialServerRecord = recordDataOriginal;
        if (recordSaveState === RecordSaveState.Update) {
          setIsRecordSaved(false);
          const status = resolveRecordStatus(nextWorkflowStepRef.current?.label, initialServerRecord.status);
          const patchData: RecordResult = createPatchData(
            formId,
            status,
            data,
            dynamicFormReference.current?.formDirtyFields() || {}
          );
          const recordChanges = {
            ...patchData,
            id: initialServerRecord.id,
            lastChange: initialServerRecord.lastChange,
          };

          const { record: finalRecord, syncDetails } = applySubmitTransforms(recordChanges, data, {
            ...transformContext,
            isUpdate: true,
          });

          saveRecord(finalRecord, syncDetails)
            .then((id) => {
              hidePerformingSaveToast();
              _onUpdateRecordSuccess();
              sender.sendData(id ?? finalRecord.id); // called to complete the onSubmit and any registered afterSubmitFormHandler's
              saveDataSubject.current?.next(finalRecord);
            })
            .catch((error) => {
              hidePerformingSaveToast();

              if (error?.status === 900) {
                if (!panelContextPropsRef.current?.batchSaveInProgress) {
                  _resolveRecordMergeConflicts(initialServerRecord, finalRecord);
                }
                notifySaveFailed(dynamicFormId.current);
              } else {
                showErrorNotification(errorMessage(error));
                log(`Failed to update record with id: ${finalRecord.id}`, { formId, error });
              }
            });
        } else if (recordSaveState === RecordSaveState.New) {
          const originalRecord = cloneDeep(initialServerRecord);
          updateOriginalData(data, originalRecord);
          const status = resolveRecordStatus(
            nextWorkflowStepRef.current?.label,
            formSettingState?.initialStep?.label.translations[0].value
          );

          const { record: createdRecord, syncDetails } = applySubmitTransforms(
            { ...originalRecord, status },
            data,
            transformContext
          );

          saveNewRecord(createdRecord, sender, syncDetails);
        }
      } else if (!defaultRecordData && !defaultLinkedRecordData && recordSaveState === RecordSaveState.New) {
        const status = resolveRecordStatus(
          nextWorkflowStepRef.current?.label,
          formSettingState?.initialStep?.label.translations[0].value
        );
        const saveData = createSaveData(formId, status, data);

        const { record: createdRecord, syncDetails } = applySubmitTransforms(saveData, data, transformContext);

        saveNewRecord(createdRecord, sender, syncDetails);
      }
      hasWorkflowStepButtonBeenClickedRef.current = false;
    },
    [
      recordDataOriginal,
      recordSaveState,
      formId,
      _onUpdateRecordSuccess,
      _resolveRecordMergeConflicts,
      defaultRecordData,
      defaultLinkedRecordData,
      formSettingState,
      saveRecord,
      showErrorNotification,
      hidePerformingSaveToast,
      recordId,
    ]
  );

  const onPostSubmit = useCallback<PostSubmitHandler>(
    (id?: OdinKey) => {
      if (id) {
        hidePerformingSaveToast();

        // Also detect temp (negative) IDs in WebView where isDbRecordId is unavailable
        const isTempId = isDbRecordId(id) || (window.ReactNativeWebView && typeof id === 'number' && id < 0);

        if (isTempId) {
          if (window.ReactNativeWebView) {
            // WebView offline save — show success, transition to update mode, keep form open
            showSuccess(t('record-saved'), {
              id: String(id),
              toasterId: isLinkedRecord ? createLinkedRecordToasterId : 'default',
            });
            notifySaveSucceeded(dynamicFormId.current);
            setNewRecordId(id as number);
            setRecordSaveState(RecordSaveState.Update);
            return;
          }
          onClose(dynamicFormId.current);
          return;
        }

        const idNumber = id as unknown as number;
        if (isLinkedRecord) {
          onLinkedRecordCreated?.(idNumber);
        }

        if (isSimpleView) {
          onSimpleViewRecordCreated?.(String(id));
        }
        showSaveNotificationAndCloseRecord(String(id));

        setNewRecordId(idNumber);
        if (isLinkedRecord) {
          setRecordSaveState(RecordSaveState.Update);
        } else {
          // scroll to last edited field, after timeout, to ensure the form has rendered
          setTimeout(() => recordRef.current?.scrollFieldIntoView((lastEditedFieldId.current || '') as string), 3000);
        }
      }
    },
    [isLinkedRecord, onLinkedRecordCreated, showSaveNotificationAndCloseRecord, onSimpleViewRecordCreated, onClose]
  );

  const onFieldChanged = useCallback(
    debounce<FieldChangedFunction>((fieldId: OdinKey) => {
      // Mark analysis as outdated if there is an existing analysis result
      if (analysisResultRef.current) {
        setIsAnalysisOutdated(true);
      }

      lastEditedFieldId.current = fieldId;
      notifyDirty(dynamicFormReference.current);
    }, 750),
    []
  );

  const onFormFieldsChanged = useCallback(
    (fields: Array<DynamicFormField>) => {
      const isHierarchiesHidden = recordPanelReadOnlyMode && formSettingState?.hierarchySettings?.hiddenInReadMode;
      const visibleSections = fields.filter((field) => field.hidden !== true);
      setVisibleGroups(visibleSections, isHierarchiesHidden);
    },
    [recordPanelReadOnlyMode, formSettingState]
  );

  const onAllGroupsExpanded = useCallback(() => {
    setShowExpandButton(false);
  }, []);

  const onAllGroupsCollapsed = useCallback(() => {
    setShowExpandButton(true);
  }, []);

  const onExpandCollapseSectionsClick = () => {
    if (showExpandButton) {
      dynamicFormReference.current?.expandAllGroups();
    } else {
      dynamicFormReference.current?.collapseAllGroups();
    }

    setShowExpandButton(!showExpandButton);
  };

  const handleCloseOfflineRecord = useCallback(() => {
    onClose(dynamicFormId.current);
    dataGridRef.current?.api.data.getDataOverwritePageCache();
  }, [onClose]);

  const onAnalyseWithAIIconClick = () => {
    const clonedFormSettingState = cloneDeep(formSettingState);
    if (clonedFormSettingState) {
      const aiAnalysisFieldIndex = clonedFormSettingState.fields.findIndex((field) => field.id === 'aiAnalysis');

      if (aiAnalysisFieldIndex !== -1) {
        clonedFormSettingState.fields[aiAnalysisFieldIndex] = {
          ...clonedFormSettingState.fields[aiAnalysisFieldIndex],
          hidden: false,
        };
        setFormSettingState(clonedFormSettingState);
        setVisibleGroups(clonedFormSettingState.fields);
      }
    }

    // Clear last edited field to prevent scrolling
    lastEditedFieldId.current = undefined;

    // Check if form needs to be saved before calling AI API
    if (recordSaveState === RecordSaveState.Update && dynamicFormReference.current?.formIsDirty()) {
      const savePromise = new Promise<void>((resolve, reject) => {
        saveDataSubject.current?.pipe(take(1), takeUntil(destroySubject.current)).subscribe({
          next: () => resolve(),
          error: (error) => reject(error),
        });
      });

      // Submit the form
      dynamicFormReference.current?.submitForm(true);

      // Wait for save to complete, then call AI API
      savePromise.finally(() => {
        startAIAnalysis(Number(recordId));
      });
    } else {
      // Form is clean, call AI API immediately
      startAIAnalysis(Number(recordId));
    }
  };

  const onCloseAIAnalysisField = useCallback(() => {
    const clonedFormSettingState = cloneDeep(formSettingState);
    if (clonedFormSettingState) {
      const aiAnalysisFieldIndex = clonedFormSettingState.fields.findIndex((field) => field.id === 'aiAnalysis');

      if (aiAnalysisFieldIndex !== -1) {
        clonedFormSettingState.fields[aiAnalysisFieldIndex] = {
          ...clonedFormSettingState.fields[aiAnalysisFieldIndex],
          hidden: true,
        };
        setFormSettingState(clonedFormSettingState);
        setVisibleGroups(clonedFormSettingState.fields);
      }
    }
  }, [formSettingState]);

  useEffect(() => {
    if (formSettingState) {
      const clonedFormSettingState = cloneDeep(formSettingState);
      const aiAnalysisFieldIndex = clonedFormSettingState.fields.findIndex((field) => field.id === 'aiAnalysis');
      if (aiAnalysisFieldIndex !== -1) {
        // Update the aiAnalysis field with the analysis result
        clonedFormSettingState.fields[aiAnalysisFieldIndex].dynamicProperties = {
          onClose: onCloseAIAnalysisField,
          analysisResult,
          isLoading: isLoadingAnalysis,
          isError: isError,
          isAnalysisOutdated: isAnalysisOutdated,
          analysisTimestamp: analysisTimestamp,
        };
      }
      setFormSettingState(clonedFormSettingState);
    }
  }, [analysisResult, isError, analysisTimestamp, isLoadingAnalysis, isAnalysisOutdated]);

  const editIconStyle = clsx('hover:text-primary-2', {
    'text-primary-2': !recordPanelReadOnlyMode,
    'hidden ': isReadOnly,
  });

  const recordButtonStyles = clsx({ 'bg-mono-1': isLinkedRecord });

  const shouldShowFeatureTour =
    featureTourData &&
    !featureTourData.hasRecordsSidebarTourRan &&
    featureTourData.hasFormSettingsTourRan &&
    featureTourData.hasSettingsMenuTourRan &&
    !isSimpleView;

  const shouldShowCloseButton = (!isSimpleView && !isLinkedRecord) || showCloseButton;
  const isRecordLoading =
    isRecordFetching ||
    isDefaultRecordDataFetching ||
    isFormSettingsDataFetching ||
    isFormAccessControlFetching ||
    isFormPermissionsLoading ||
    isFieldAndSectionVisibilitiesLoading;

  return (
    <>
      <div
        className="z-20 flex flex-shrink-0 flex-col"
        data-testid="record-header"
        data-edit-mode={!recordPanelReadOnlyMode}
      >
        <div className="flex">
          {(!isLinkedRecord || (isLinkedRecord && recordSaveState === RecordSaveState.Update)) && (
            <FormTitle title={recordTitle.elementTitle} />
          )}
          <div
            className={clsx('flex items-center gap-2', {
              'w-full justify-end':
                !recordTitle.stringTitle || (isLinkedRecord && recordSaveState === RecordSaveState.New),
            })}
            ref={tagCreator('all-icon')}
          >
            {showAnalyzeIcon && recordId && (
              <Tooltip
                tooltipClassName="bg-mono-1"
                description={String(t('analyse-with-ai', 'Analyse with AI'))}
                debounceTime={200}
                tooltipZIndexCheck
              >
                <IconButton
                  disabled={isLoadingAnalysis}
                  icon="FlaskConical"
                  iconSize={OdinIconSize.Small}
                  classNames="hover:text-primary-2"
                  onClick={onAnalyseWithAIIconClick}
                />
              </Tooltip>
            )}
            <Tooltip
              tooltipClassName="bg-mono-1"
              description={showExpandButton ? String(t('expand-all')) : String(t('collapse-all'))}
              debounceTime={200}
              tooltipZIndexCheck
            >
              <IconButton
                icon={showExpandButton ? 'ChevronsDownUp' : 'ChevronsUpDown'}
                iconSize={OdinIconSize.Medium}
                classNames="hover:text-primary-2"
                onClick={onExpandCollapseSectionsClick}
              />
            </Tooltip>
            {recordSaveState !== RecordSaveState.New && !isLinkedRecord && (
              <>
                {!isSimpleView && (
                  <div className="group relative flex items-center" ref={tagCreator('more-icon')}>
                    {!panelContextProps?.fullScreen && !isRecordFetching && (
                      <IconButton
                        icon="Ellipsis"
                        iconSize={OdinIconSize.Medium}
                        classNames="group-hover:text-primary-2"
                      />
                    )}
                    {!isRecordFetching && (
                      <RecordMenu
                        recordId={recordId}
                        recordTitle={recordTitle.stringTitle}
                        panelContextProps={panelContextProps}
                        hasDeletePermission={currentRecordPermissions?.delete ?? false}
                        hasViewRecordAuditPermission={currentRecordPermissions?.viewRecordAudit ?? false}
                        onClose={onClose}
                        onRefreshRecordClick={onRefreshRecord}
                        isRecordReadOnly={recordPanelReadOnlyMode}
                        formId={formId}
                        vikingLinkUrl={vikingLinkUrl}
                        formGroups={formGroups}
                      />
                    )}
                  </div>
                )}
                {canEditRecord && showEditButton && (
                  <Tooltip description={String(t('edit'))} tooltipClassName="bg-mono-1" debounceTime={200}>
                    <IconButton
                      icon="Pencil"
                      iconSize={OdinIconSize.Small}
                      classNames={editIconStyle}
                      onClick={onRecordsEdit}
                    />
                  </Tooltip>
                )}
              </>
            )}
            {shouldShowCloseButton && <CloseActiveItemButton size={OdinIconSize.Medium} onClick={onRecordClose} />}
          </div>
        </div>
        {singleRecordData?.original?.accountable && formSettingsData?.notificationBadgeAlertsEnabled && (
          <RecordAccountable accountablePeople={singleRecordData?.original?.accountable} />
        )}
        {isOfflineEnabled && (
          <RecordOffline
            recordId={recordId}
            metadata={singleRecordData?.original._offlineMetadata}
            saveRecord={saveRecordCallback}
            close={handleCloseOfflineRecord}
            formIsDirty={() => dynamicFormReference.current?.formIsDirty()}
          />
        )}
      </div>
      <div
        ref={tagCreator('content')}
        className={clsx(
          'relative flex flex-grow flex-row',
          { 'mt-6 overflow-y-hidden': !isLinkedRecord },
          { 'h-[calc(100%-4rem)]': isLinkedRecord } // allow space for form buttons
        )}
        data-record-loading={isRecordLoading}
      >
        {formGroups && (panelContextProps?.fullScreen || isLinkedRecord || showFormGroupsNavigation) && (
          <FormGroupNavigation formGroups={formGroups} onGroupItemClicked={onGroupItemClicked} />
        )}
        {isRecordLoading && <FormLoading />}
        {formSettingState && formButtonsState && recordDataFlat && (
          <DynamicForm
            ref={dynamicFormReference}
            dynamicFormId={dynamicFormId.current}
            settings={formSettingState}
            dataRetrieval={dataRetrieval}
            data={recordDataFlat}
            onGetPreSubmitData={onGetPreSubmitData}
            onPreSubmit={onPreSubmit}
            onSubmit={onFormSubmit}
            onPostSubmit={onPostSubmit}
            onError={onError}
            showSubmitButtons={true}
            readOnly={recordPanelReadOnlyMode}
            buttons={formButtonsState}
            buttonStyles={recordButtonStyles}
            buttonsPosition={1}
            onFieldChanged={onFieldChanged}
            onFormFieldsChanged={onFormFieldsChanged}
            onAllGroupsExpanded={onAllGroupsExpanded}
            onAllGroupsCollapsed={onAllGroupsCollapsed}
          />
        )}
      </div>
      <WorkflowStepModal ref={nextWorkflowStepModalReference} />
      <ConflictsModal ref={conflictsModalRef} recordId={recordId} />
      <ConfirmationDialog
        ref={saveModalReference}
        onConfirm={onSaveConfirm}
        onClose={onSaveConfirmationCancel}
        cancelButtonText={t('discard')}
        hasCancelButton
      />
      {shouldShowFeatureTour && <RecordSideBarFeatureTour featureTourData={featureTourData} />}
    </>
  );
}

export default forwardRef(Record);
